import { Service } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

type AllowedTypes = string | number | boolean | object;

/**
 * Wrapper for storing values in any type of storage. Supports expiration / TTL
 */
type StorageWrapper<T extends AllowedTypes> = {
  value: T;
  expiresAt: number | null;
};

/**
 * Interface for storage service
 */
interface IStorageService {
  set<T extends AllowedTypes>(key: string, value: T, ttl?: number): void;
  get<T extends AllowedTypes>(key: string): T | null;
  remove(key: string): void;
}

/**
 * Abstract class for storage service
 */
export abstract class Storage implements IStorageService {
  set<T extends AllowedTypes>(_key: string, _value: T, _ttl?: number): void {
    throw new Error('Method not implemented.');
  }
  get<T extends AllowedTypes>(_key: string): T | null {
    throw new Error('Method not implemented.');
  }
  exists(_key: string): boolean {
    throw new Error('Method not implemented.');
  }

  remove(_key: string): void {
    throw new Error('Method not implemented.');
  }
}

/**
 * Service for storing values in session storage. If ttl is not provided, the value will not be cleared until the browser is closed.
 */
@Service({ autoProvided: false })
export class SessionStorage implements Storage {
  set<T extends AllowedTypes>(key: string, value: T, ttl?: number): void {
    if (!key) {
      throw new Error('Key is required');
    }
    if (!value) {
      throw new Error('Value is required');
    }

    const wrappedValue: StorageWrapper<T> = {
      value,
      expiresAt: ttl ? Date.now() + ttl : null
    };

    sessionStorage.setItem(key, JSON.stringify(wrappedValue));
  }

  get<T extends AllowedTypes>(key: string): T | null {
    try {
      const item = sessionStorage.getItem(key);
      if (!item) {
        return null;
      }
      const wrappedValue = JSON.parse(item);
      if (!wrappedValue?.value) {
        return null;
      }

      // Side effect: remove the item if it has expired
      if (wrappedValue.expiresAt && wrappedValue.expiresAt < Date.now()) {
        sessionStorage.removeItem(key);
        return null;
      }

      return wrappedValue.value;
    } catch (e) {
      // TODO: replace with log provider
      console.error(e);
      return null;
    }
  }

  exists(key: string): boolean {
    return !!sessionStorage.getItem(key);
  }

  remove(key: string): void {
    sessionStorage.removeItem(key);
  }
}

/**
 * Service for storing values in local storage.
 * If TTL is not provided, the value will not be cleared until the localstorage (or the specific) key is cleared manually.
 */
@Service({ autoProvided: false })
export class LocalStorage implements Storage {
  set<T extends AllowedTypes>(key: string, value: T, ttl?: number): void {
    const wrappedValue: StorageWrapper<T> = {
      value,
      expiresAt: ttl ? Date.now() + ttl : null
    };

    localStorage.setItem(key, JSON.stringify(wrappedValue));
  }
  get<T extends AllowedTypes>(key: string): T | null {
    try {
      const item = localStorage.getItem(key);
      if (!item) {
        return null;
      }
      const wrappedValue = JSON.parse(item);
      if (!wrappedValue?.value) {
        return null;
      }

      // Side effect: remove the item if it has expired
      if (wrappedValue.expiresAt && wrappedValue.expiresAt < Date.now()) {
        localStorage.removeItem(key);
      }
      return wrappedValue.value;
    } catch (e) {
      // TODO: replace with log provider
      console.error(e);
      return null;
    }
  }

  exists(key: string): boolean {
    return !!localStorage.getItem(key);
  }

  remove(key: string): void {
    localStorage.removeItem(key);
  }
}

/**
 * Service for storing values in memory. Values will be cleared when the service is destroyed.
 * Usually when the application is "restarted".
 */
@Service({ autoProvided: false })
export class InMemoryStorage implements Storage {
  private _storage$ = new BehaviorSubject<Record<string, unknown> | null>(null);

  set<T extends AllowedTypes>(key: string, value: T, ttl?: number): void {
    const storage = this._storage$.value ?? {};
    storage[key] = {
      value,
      expiresAt: ttl ? Date.now() + ttl : null
    };

    this._storage$.next(storage);
  }
  get<T extends AllowedTypes>(key: string): T | null {
    const storageSnapshot = this._storage$.value;
    if (!storageSnapshot) {
      return null;
    }
    const storageItem = storageSnapshot[key] as StorageWrapper<T> | null;
    if (!storageItem) {
      return null;
    }

    if (!storageItem?.value) {
      return null;
    }

    // side effect: remove the item if it has expired
    if (storageItem.expiresAt && storageItem.expiresAt < Date.now()) {
      this.remove(key);
      return null;
    }

    return storageItem.value;
  }

  exists(key: string): boolean {
    return !!this._storage$.value?.[key];
  }

  remove(key: string): void {
    const storage = this._storage$.value ?? {};
    delete storage[key];
    this._storage$.next(storage);
  }
}
